import Sidebar from '@/components/Home_Sidebar/sidebarView.vue';
import Navbar from '@/components/Home_Navbar/NavbarView.vue';
import PoweredBy from '@/components/PoweredBy/PoweredByView.vue';
import { get_token } from '@/router/index.js';
import { embedDashboard } from '@superset-ui/embedded-sdk';

const DASHBOARD_IDS = Object.freeze({
  management: 'bbd5ab6c-67cb-4555-8adb-b9df9e81b410',
  onboarding: '529a0897-ea11-4a79-94ee-19ab183a09be',
});

export default {
  name: 'HomeView',
  components: { Sidebar, Navbar, PoweredBy },
  data: () => ({
    loading: false,
    error_message: '',
    user: { name: 'Loading...', first_name: '', last_name: '', email: '' },
    active_tab: 'management',
    embedded_dashboard: null,
    embed_request_id: 0,
  }),
  async mounted() {
    await this.load_user_details();
    await this.load_superset_dashboard();
  },
  beforeUnmount() {
    this.embed_request_id += 1;
    this.unmount_dashboard();
  },
  methods: {
    async switch_tab(tab) {
      if (!Reflect.has(DASHBOARD_IDS, tab) || tab === this.active_tab) return;
      this.active_tab = tab;
      await this.load_superset_dashboard();
    },
    selected_dashboard_id() {
      return Reflect.get(DASHBOARD_IDS, this.active_tab);
    },
    unmount_dashboard() {
      if (this.embedded_dashboard?.unmount) this.embedded_dashboard.unmount();
      this.embedded_dashboard = null;
      this.$refs.superset_mount?.replaceChildren();
    },
    async load_user_details() {
      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return;
      }
      try {
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_current_user/`, {
          headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch user');
        const user = (await response.json()).data || {};
        this.user = {
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          email: user.email || '',
          name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User',
        };
      } catch {
        this.user.name = 'User';
      }
    },
    async fetch_guest_token(dashboard_id) {
      const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/aic_one_api/superset_management/api/get_superset_guest_token_api/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${get_token()}` },
        body: JSON.stringify({ dashboard_id, user: this.user }),
      });
      const json = await response.json();
      if (!response.ok || json.status !== 'success') {
        throw new Error(json.message || 'Failed to get Superset guest token');
      }
      return json.data;
    },
    async load_superset_dashboard() {
      const dashboard_id = this.selected_dashboard_id();
      const request_id = this.embed_request_id + 1;
      this.embed_request_id = request_id;
      this.loading = true;
      this.error_message = '';
      this.unmount_dashboard();
      try {
        const guest_data = await this.fetch_guest_token(dashboard_id);
        if (request_id !== this.embed_request_id) return;
        if (!guest_data?.token) throw new Error('Guest token missing from backend response');
        if (!guest_data?.superset_domain) throw new Error('Superset domain missing from backend response');
        const mount_point = this.$refs.superset_mount;
        if (!mount_point) throw new Error('Dashboard mount point not found');
        mount_point.innerHTML = '';
        const embedded_dashboard = await embedDashboard({
          id: dashboard_id,
          supersetDomain: guest_data.superset_domain.replace(/\/$/, ''),
          mountPoint: mount_point,
          fetchGuestToken: async () => guest_data.token,
          dashboardUiConfig: {
            hideTitle: true,
            hideChartControls: true,
            hideTab: false,
            filters: { expanded: false },
            urlParams: { standalone: '1' },
          },
        });
        if (request_id !== this.embed_request_id) {
          embedded_dashboard.unmount();
          return;
        }
        this.embedded_dashboard = embedded_dashboard;
      } catch (error) {
        if (request_id !== this.embed_request_id) return;
        this.error_message = error.message || 'Unable to load embedded dashboard';
      } finally {
        if (request_id === this.embed_request_id) this.loading = false;
      }
    },
  },
};
